import {DatePicker, Form, Input, Modal, Select, Spin} from 'antd';
import TextArea from 'antd/es/input/TextArea';
import dayjs from 'dayjs';
import PropTypes from 'prop-types';
import {useEffect} from 'react';
import toast from 'react-hot-toast';
import {useSelector} from 'react-redux';

import {RqAddOffers, RqGetAllProducts, RqUpdateOffers} from '../../../../reactQuery/apis';
import {selectAuth} from '../../../../redux/selectors';

const OffersModal = ({open, onCancel, refetch, updateRecord}) => {
  const [form] = Form.useForm();

  const auth = useSelector(selectAuth);
  
  const token = auth?.tokenData;

  const {data:allProducts} = RqGetAllProducts(token);

  const productOptions = allProducts?.map(product => ({
    label: product.productName,
    value: product.id,
  })) || [];

  const {mutate: addOffers, isLoading} = RqAddOffers({
    onSuccess: () => {
      toast.success('Offers added successfully');
      refetch();
      form.resetFields();
      onCancel();
    },
    onError: error => {
      toast.error(error?.response?.data?.message || 'Failed to add');
    },
  });

  const {mutate: updateOffers} = RqUpdateOffers({
    onSuccess: () => {
      toast.success('Offers updated successfully');
      refetch();
      form.resetFields();
      onCancel();
    },
    onError: error => {
      toast.error(error?.response?.data?.message || 'Update failed');
    },
  });

  useEffect(() => {
    if (updateRecord) {
      form.setFieldsValue({
        name: updateRecord.name,
        value: updateRecord.value,
        minOrderAmount: updateRecord.minOrderAmount,
        maxDiscount: updateRecord.maxDiscount,
        validFrom: updateRecord.validFrom ? dayjs(updateRecord.validFrom) : null,
        validUntil: updateRecord.validUntil ? dayjs(updateRecord.validUntil) : null,
        applicableProductIds: updateRecord?.applicableProductIds 
        || updateRecord?.applicableProducts?.map(p => p.id) 
        || [],
        description: updateRecord.description,
        type: updateRecord.type || 'PERCENTAGE',
      });
    } else {
      form.resetFields();
    }
  }, [updateRecord]);

  const handleFinish = values => {
    const formattedData = {
      name: values.name,
      description: values.description,
      type: values.type || 'PERCENTAGE',          
      minOrderAmount: Number(values.minOrderAmount),
      maxDiscount: Number(values.maxDiscount),
      validFrom: values.validFrom ? values.validFrom.format('YYYY-MM-DDTHH:mm:ss') : null,
      validUntil: values.validUntil ? values.validUntil.format('YYYY-MM-DDTHH:mm:ss') : null,
      applicableProductIds: values.applicableProductIds,
      value:values.value,
    };

    if (updateRecord) {
      updateOffers({
        token,
        id: updateRecord.id,
        formData: formattedData,
      });
    } else {
      addOffers({
        token,
        formData: formattedData,
      });
    }
  };

  if (isLoading) {
    return (
      <div className={'flex justify-center items-center'}>
        <Spin size={'large'} />
      </div>
    );
  }

  return (
    <Modal
      title={updateRecord ? 'Update Offer' : 'Add Offer'}
      open={open}
      width={600}
      onCancel={() => {
        form.resetFields();
        onCancel();
      }}
      onOk={() => form.submit()}
      centered
      okText={updateRecord ? 'Update' : 'Save'}
      cancelText={'Cancel'}
      okButtonProps={{
        className: '!bg-primary-900 !text-white hover:!bg-primary-800',
      }}
    >
      <Form
        form={form}
        layout={'vertical'}
        onFinish={handleFinish}
        className={'flex flex-col gap-3'}
      >
        <div className={'grid grid-cols-2 gap-2'}>
          <Form.Item
            label={'Name'}
            name={'name'}
            rules={[{required: true, message: 'Please enter the offer name'}]}
          >
            <Input placeholder={'Enter offer name'} />
          </Form.Item>

          <Form.Item
            label={'Value (%)'}
            name={'value'}
            rules={[
              {required: true, message: 'Please enter offer value'},
            ]}
          >
            <Input type={'number'} placeholder={'Enter discount value'} />
          </Form.Item>

          <Form.Item
            label={'Minimum Order Amount'}
            name={'minOrderAmount'}
            rules={[{required: true, message: 'Please enter minimum order amount'}]}
          >
            <Input type={'number'} placeholder={'Enter minimum order amount'} min={0} />
          </Form.Item>

          <Form.Item
            label={'Maximum Discount'}
            name={'maxDiscount'}
            rules={[{required: true, message: 'Please enter maximum discount'}]}
          >
            <Input type={'number'} placeholder={'Enter maximum discount'} min={0} />
          </Form.Item>

          <Form.Item
            label={'Valid From'}
            name={'validFrom'}
            rules={[{required: true, message: 'Please select valid from date'}]}
          >
            <DatePicker
              className={'w-full'}
              format={'YYYY-MM-DD'}
              placeholder={'Select valid from date'}
            />
          </Form.Item>

          <Form.Item
            label={'Valid Until'}
            name={'validUntil'}
            rules={[{required: true, message: 'Please select valid until date'}]}
          >
            <DatePicker
              className={'w-full'}
              format={'YYYY-MM-DD'}
              placeholder={'Select valid until date'}
            />
          </Form.Item>
        </div>

        <Form.Item
          label={'Applicable Products'}
          name={'applicableProductIds'}
          rules={[{required: true, message: 'Please select applicable products'}]}
        >
          <Select
            mode={'multiple'}
            placeholder={'Select applicable products'}
            options={productOptions}
          />
        </Form.Item>

        <Form.Item
          label={'Description'}
          name={'description'}
          rules={[{required: true, message: 'Please enter description'}]}
        >
          <TextArea rows={4} placeholder={'Enter offer description'} />
        </Form.Item>
      </Form>
    </Modal>
  );
};

OffersModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onCancel: PropTypes.func.isRequired,
  refetch: PropTypes.func,
  updateRecord: PropTypes.object,
};

export default OffersModal;
